import type {
  Collection,
  CollectionId,
  DayNote,
  Entitlement,
  Recipe,
  RecipeId,
  RecipeStore,
  RevisionSummary,
  TagDisplayCase,
} from '@platewell/core';
import {
  DEFAULT_TAG_DISPLAY_CASE,
  FREE_ENTITLEMENT,
  MAIN_COLLECTION_ID,
  formatTagForDisplay,
  indexDayNotesByDay,
} from '@platewell/core';
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { listContextQuery, searchListContext, type ListContext } from './list-context.js';
import { usePlatewell } from './provider.js';
import {
  browse,
  collectionSummaries,
  recipeQuota,
  tagFacets,
  toSummary,
  trashed,
  type BrowseQuery,
  type CollectionSummary,
  type RecipeQuotaView,
  type RecipeSummary,
  type TagFacet,
} from './selectors.js';

/**
 * A monotonically increasing counter that ticks on every document change.
 *
 * Derived views are recomputed from this rather than returned directly from
 * `useSyncExternalStore`, because the store builds fresh arrays on every read and React
 * would see a new reference each time it checked the snapshot — an infinite render loop.
 * A number is a stable snapshot; the actual data hangs off `useMemo` keyed by it.
 */
export function useStoreVersion(store: RecipeStore | null): number {
  const version = useRef(0);

  const subscribe = useCallback(
    (onChange: () => void) => {
      if (store == null) return () => {};
      return store.subscribeAll(() => {
        version.current += 1;
        onChange();
      });
    },
    [store],
  );

  return useSyncExternalStore(
    subscribe,
    () => version.current,
    () => version.current,
  );
}

export function useStore(): RecipeStore {
  const { store } = usePlatewell();
  if (store == null) throw new Error('no household is open yet; render a loading state first');
  return store;
}

/** Null-tolerant variant for shells that render before a household has finished opening. */
export function useOptionalStore(): RecipeStore | null {
  return usePlatewell().store;
}

export function useRecipes(query: BrowseQuery = {}): Recipe[] {
  const { store, search } = usePlatewell();
  const version = useStoreVersion(store);
  // Callers pass a fresh object literal on every render, so the query is compared by value.
  // Depending on its identity instead would recompute the whole list each time.
  const key = JSON.stringify(query);
  const parsed = useMemo(() => JSON.parse(key) as BrowseQuery, [key]);
  return useMemo(
    () => (store == null ? [] : browse(store, search, parsed)),
    [store, search, parsed, version],
  );
}

export function useRecipeSummaries(query: BrowseQuery = {}): RecipeSummary[] {
  const recipes = useRecipes(query);
  return useMemo(() => recipes.map(toSummary), [recipes]);
}

/** See {@link searchListContext}. */
export function useRecipesInList(context: ListContext, term: string): RecipeSummary[] {
  const { store, search } = usePlatewell();
  const version = useStoreVersion(store);
  const key = JSON.stringify(context);
  const parsed = useMemo(() => JSON.parse(key) as ListContext, [key]);
  // Read once per list rather than once per keystroke; only the search follows the term.
  const listed = useMemo(
    () => (store == null ? [] : browse(store, search, listContextQuery(parsed))),
    [store, search, parsed, version],
  );
  return useMemo(
    () =>
      store == null
        ? []
        : searchListContext(store, search, parsed, term, listed).map(toSummary),
    [store, search, parsed, term, listed],
  );
}

export function useRecipe(id: RecipeId | null | undefined): Recipe | null {
  const { store } = usePlatewell();
  const version = useStoreVersion(store);
  return useMemo(
    () => (store == null || id == null ? null : store.getRecipe(id)),
    [store, id, version],
  );
}

export function useCollections(): CollectionSummary[] {
  const { store } = usePlatewell();
  const version = useStoreVersion(store);
  return useMemo(() => (store == null ? [] : collectionSummaries(store)), [store, version]);
}

export function useCollection(id: string | null | undefined): Collection | null {
  const { store } = usePlatewell();
  const version = useStoreVersion(store);
  return useMemo(
    () => (store == null || id == null ? null : store.getCollection(id)),
    [store, id, version],
  );
}

export function useTags(limit = 50): TagFacet[] {
  const { store } = usePlatewell();
  const version = useStoreVersion(store);
  return useMemo(() => (store == null ? [] : tagFacets(store, limit)), [store, limit, version]);
}

/** The household's day notes for the schedule, grouped by YYYY-MM-DD. */
export function useDayNotes(): ReadonlyMap<string, readonly DayNote[]> {
  const { store } = usePlatewell();
  const version = useStoreVersion(store);
  return useMemo(
    () => (store == null ? EMPTY_DAY_NOTES : indexDayNotesByDay(store.listDayNotes())),
    [store, version],
  );
}

const EMPTY_DAY_NOTES: ReadonlyMap<string, readonly DayNote[]> = new Map();

export function useTagDisplayCase(): TagDisplayCase {
  const { store } = usePlatewell();
  const version = useStoreVersion(store);
  return useMemo(() => store?.getTagDisplayCase() ?? DEFAULT_TAG_DISPLAY_CASE, [store, version]);
}

/**
 * The household's plan, from the document.
 *
 * Driven by `useStoreVersion` like `useTagDisplayCase`, so the moment the Durable Object's
 * heal or a webhook push lands in `meta` the whole tree re-renders and the cap lifts —
 * no reload, no refetch.
 */
export function useEntitlement(): Entitlement {
  const { store } = usePlatewell();
  const version = useStoreVersion(store);
  return useMemo(() => store?.getEntitlement() ?? FREE_ENTITLEMENT, [store, version]);
}

export function useRecipeQuota(): RecipeQuotaView {
  const { store } = usePlatewell();
  const version = useStoreVersion(store);
  return useMemo(
    () =>
      store == null
        ? {
            limit: null,
            used: 0,
            remaining: null,
            atLimit: false,
            atCeiling: false,
            canAdd: true,
            approaching: false,
          }
        : recipeQuota(store),
    [store, version],
  );
}

/** Only owners may subscribe or manage billing, so every paywall CTA is gated on this. */
export function useIsOwner(): boolean {
  return usePlatewell().role === 'owner';
}

export function useImportCollectionId(): CollectionId {
  const { store } = usePlatewell();
  const version = useStoreVersion(store);
  return useMemo(() => store?.getImportCollectionId() ?? MAIN_COLLECTION_ID, [store, version]);
}

export function useFormatTag(): (tag: string) => string {
  const mode = useTagDisplayCase();
  return useCallback((tag: string) => formatTagForDisplay(tag, mode), [mode]);
}

export function useTrash(): Recipe[] {
  const { store } = usePlatewell();
  const version = useStoreVersion(store);
  return useMemo(() => (store == null ? [] : trashed(store)), [store, version]);
}

export function useIsFavourite(recipeId: RecipeId | null | undefined): boolean {
  const { store } = usePlatewell();
  const version = useStoreVersion(store);
  return useMemo(
    () => (store == null || recipeId == null ? false : store.isFavourite(recipeId)),
    [store, recipeId, version],
  );
}

export function useToggleFavourite(): (recipeId: RecipeId, favourite?: boolean) => void {
  const { store } = usePlatewell();
  const version = useStoreVersion(store);
  return useCallback(
    (recipeId: RecipeId, favourite?: boolean) => {
      if (store == null) return;
      const next = favourite ?? !store.isFavourite(recipeId);
      void store.setFavourite(recipeId, next);
    },
    [store, version],
  );
}

export function useSuggestions(term: string, limit = 8): string[] {
  const { search, store } = usePlatewell();
  const version = useStoreVersion(store);
  return useMemo(() => search?.suggest(term, limit) ?? [], [search, term, limit, version]);
}

/**
 * Debounced search term. Typing re-runs MiniSearch and re-renders the list, which is fine
 * for a few hundred recipes and visibly janky for a few thousand.
 */
export function useDebounced<T>(value: T, delayMs = 150): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

export function useConnectionStatus() {
  const { status, role } = usePlatewell();
  return {
    status,
    online: status === 'synced' || status === 'connected' || status === 'read_only',
    /** True while local edits exist that the server has not acknowledged. */
    pending: status !== 'synced' && status !== 'read_only',
    readOnly: status === 'read_only' || role === 'viewer',
  };
}

/**
 * True while a household is open but this device has never received it from the server —
 * a first sign-in on a new device. An empty list then means "still arriving", not "none".
 */
export function useAwaitingFirstSync(): boolean {
  const { store, hasSynced } = usePlatewell();
  return store != null && !hasSynced;
}

export function useHouseholds() {
  const { households, activeHouseholdId, setActiveHousehold, reloadHouseholds, loading } =
    usePlatewell();
  const active = households.find((h) => h.id === activeHouseholdId) ?? null;
  return { households, active, activeHouseholdId, setActiveHousehold, reloadHouseholds, loading };
}

/**
 * Revision history is server-derived, so unlike everything else in this module it is an
 * async fetch and is simply unavailable offline.
 */
export function useRevisions(recipeId: RecipeId | null): {
  revisions: RevisionSummary[];
  loading: boolean;
  error: Error | null;
} {
  const { store } = usePlatewell();
  const [revisions, setRevisions] = useState<RevisionSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (store == null || recipeId == null) {
      setRevisions([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    store
      .listRevisions(recipeId)
      .then((list) => {
        if (!cancelled) setRevisions(list);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof Error ? cause : new Error(String(cause)));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [store, recipeId]);

  return { revisions, loading, error };
}

/**
 * The name of the list a recipe was opened from, for a back link or a page heading.
 *
 * Returns null when there is no context, and also when a collection has since been deleted —
 * the caller should hide the affordance rather than link somewhere that no longer exists.
 */
export function useListContextLabel(context: ListContext | null): string | null {
  const collection = useCollection(context?.kind === 'collection' ? context.collectionId : null);
  const formatTag = useFormatTag();
  if (context == null) return null;
  switch (context.kind) {
    case 'collection':
      return collection?.name ?? null;
    case 'favourites':
      return 'Favourites';
    case 'recent':
      return 'Recently Added';
    case 'tag':
      return `Tagged “${formatTag(context.tag)}”`;
    case 'search':
      return `Results for “${context.term}”`;
    case 'all':
      return 'All Recipes';
  }
}
